<script setup lang="ts">
  import BoldFontIcon from '@/shared/assets/icons/BoldFontIcon.svg';
  import ItalicFontIcon from '@/shared/assets/icons/ItalicFontIcon.svg';
  import StrikethroughFontIcon from '@/shared/assets/icons/StrikethroughFontIcon.svg';
  import UnderscoreFontIcon from '@/shared/assets/icons/UnderscoreFontIcon.svg';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    IconImage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/shared/ui';
  import TextAlignLeftIcon from '@/shared/assets/icons/TextAlignLeftIcon.svg';
  import TextAlignCenterIcon from '@/shared/assets/icons/TextAlignCenterIcon.svg';
  import TextAlignRightIcon from '@/shared/assets/icons/TextAlignRightIcon.svg';
  import TextAlignJustifyIcon from '@/shared/assets/icons/TextAlignJustifyIcon.svg';
  import { computed, onMounted, ref, toRef, watch } from 'vue';
  import * as z from 'zod';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import { DEFAULT_COLORS, DEFAULT_FONT, DEFAULT_SPACING } from '../../../lib/element-defaults';
  import type { ElementProperties } from '../../../model/form.types';
  import { useElementProperties } from '../../../model/useElementProperties';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import { useTextFormatToggles } from '../../../model/useTextFormatToggles';
  import MarginControl from './MarginControl.vue';

  interface Props {
    elementId?: string;
    section?: 'design' | 'extras';
  }

  const props = withDefaults(defineProps<Props>(), {
    section: 'design',
  });

  const isDesignSection = computed(() => props.section === 'design');
  const isExtrasSection = computed(() => props.section === 'extras');

  const { selectedElement } = useElementProperties(() => props.elementId);

  const INPUT_ELEMENT_TYPES: Set<string> = new Set([
    'TEXT_INPUT',
    'PARAGRAPH',
    'NUMBER_INPUT',
    'DATE_INPUT',
    'DROPDOWN',
    'URL_INPUT',
    'EMAIL_INPUT',
    'REGISTER_NUMBER_INPUT',
  ]);

  const isInputElement = computed(() => {
    return selectedElement.value?.type
      ? INPUT_ELEMENT_TYPES.has(selectedElement.value.type)
      : false;
  });

  const isFixedTextElement = computed(() => selectedElement.value?.type === 'FIXED_TEXT');

  const marginValues = computed(() => ({
    top: values?.marginTop ?? 0,
    right: values?.marginRight ?? 0,
    bottom: values?.marginBottom ?? 0,
    left: values?.marginLeft ?? 0,
  }));

  function onMarginUpdate(margin: { top: number; right: number; bottom: number; left: number }) {
    setValues({
      marginTop: margin.top,
      marginRight: margin.right,
      marginBottom: margin.bottom,
      marginLeft: margin.left,
    });
  }

  const headingFontSizes = {
    h1: 12,
    h2: 11,
    h3: 10,
    h4: 18,
    h5: 16,
    h6: 14,
    text4: 9,
    text5: 8,
  } as const;

  type HeadingLevel = keyof typeof headingFontSizes;

  const fontSizeToHeading = (fontSize?: number): HeadingLevel | undefined => {
    if (!fontSize) return 'h3';
    const entries = Object.entries(headingFontSizes) as [HeadingLevel, number][];
    const closest = entries.reduce((prev, curr) => {
      return Math.abs(curr[1] - fontSize) < Math.abs(prev[1] - fontSize) ? curr : prev;
    });
    return closest[0];
  };

  const headingToFontSize = (heading: HeadingLevel | string | undefined): number => {
    if (!heading || !(heading in headingFontSizes)) return DEFAULT_FONT.size;
    return headingFontSizes[heading as HeadingLevel];
  };

  const TEXT_TYPE_SIZES = {
    simple: 10,
    h1: 32,
    h2: 24,
    h3: 19,
  } as const;

  type TextType = keyof typeof TEXT_TYPE_SIZES;

  function fontSizeToTextType(fontSize?: number): TextType {
    if (!fontSize || fontSize <= 10) return 'simple';
    if (fontSize >= 30) return 'h1';
    if (fontSize >= 22) return 'h2';
    if (fontSize >= 17) return 'h3';
    return 'simple';
  }

  type FormValues = {
    width?: number;
    fontSize?: number;
    headingLevel?: HeadingLevel;
    textType?: TextType;
    fontWeight?: 'normal' | 'semibold' | 'bold';
    fontStyle?: 'normal' | 'italic';
    textDecoration?: 'none' | 'underline' | 'line-through';
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    color?: string;
    backgroundColor?: string;
    paddingUniform?: number;
    marginTop?: number;
    marginRight?: number;
    marginBottom?: number;
    marginLeft?: number;
    tooltip?: string;
    logFieldName?: string;
  };

  const { values, setValues } = usePropertyForm<FormValues, ElementProperties>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      width: z.number().min(10).max(100).optional(),
      fontSize: z.number().min(8).max(72).optional(),
      headingLevel: z.enum(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'text4', 'text5']).optional(),
      textType: z.enum(['simple', 'h1', 'h2', 'h3']).optional(),
      fontWeight: z.enum(['normal', 'semibold', 'bold']).optional(),
      fontStyle: z.enum(['normal', 'italic']).optional(),
      textDecoration: z.enum(['none', 'underline', 'line-through']).optional(),
      textAlign: z.enum(['left', 'center', 'right', 'justify']).optional(),
      color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Cor inválida (use formato #RRGGBB)')
        .optional(),
      backgroundColor: z
        .string()
        .refine(
          (val) => val === 'transparent' || /^#[0-9A-Fa-f]{6}$/.test(val),
          'Cor inválida (use formato #RRGGBB ou transparent)'
        )
        .optional(),
      paddingUniform: z.number().min(0).max(100).optional(),
      marginTop: z.number().min(0).max(100).optional(),
      marginRight: z.number().min(0).max(100).optional(),
      marginBottom: z.number().min(0).max(100).optional(),
      marginLeft: z.number().min(0).max(100).optional(),
      tooltip: z.string().max(FORM_FIELD_CHARACTER_LIMITS.TOOLTIP).optional(),
      logFieldName: z.string().max(FORM_FIELD_CHARACTER_LIMITS.LOG_FIELD_NAME).optional(),
    }),
    extractInitialValues: (properties: ElementProperties | undefined): FormValues => {
      const fontSize = properties?.fontSize ?? (isFixedTextElement.value ? 0 : DEFAULT_FONT.size);
      return {
        width: properties?.width ?? 100,
        fontSize,
        headingLevel: fontSizeToHeading(fontSize),
        textType: fontSizeToTextType(fontSize),
        fontWeight: properties?.fontWeight ?? DEFAULT_FONT.weight,
        fontStyle: properties?.fontStyle ?? DEFAULT_FONT.style,
        textDecoration: properties?.textDecoration ?? DEFAULT_FONT.decoration,
        textAlign: (properties?.textAlign as 'left' | 'center' | 'right' | 'justify') ?? 'left',
        color: properties?.color ?? DEFAULT_COLORS.text,
        backgroundColor: properties?.backgroundColor ?? DEFAULT_COLORS.background,
        paddingUniform: properties?.padding?.top ?? DEFAULT_SPACING.padding.top,
        marginTop: properties?.margin?.top ?? 0,
        marginRight: properties?.margin?.right ?? 0,
        marginBottom: properties?.margin?.bottom ?? 0,
        marginLeft: properties?.margin?.left ?? 0,
        tooltip: properties?.tooltip ?? '',
        logFieldName: properties?.logFieldName ?? '',
      };
    },
    mapValuesToProperties: (values: FormValues, currentProperties: ElementProperties) => {
      if (isExtrasSection.value) {
        return {
          properties: {
            ...currentProperties,
            tooltip: values.tooltip || undefined,
            logFieldName: values.logFieldName || undefined,
          },
        };
      }

      const {
        width,
        paddingUniform,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
        fontSize,
        headingLevel,
        fontWeight,
        fontStyle,
        textDecoration,
        textAlign,
        color,
        backgroundColor,
      } = values;

      const fallbackFontSize = fontSize || DEFAULT_FONT.size;
      const finalFontSize =
        !isFixedTextElement.value && headingLevel
          ? headingToFontSize(headingLevel)
          : fallbackFontSize;

      const fontProperties = {
        fontSize:
          isFixedTextElement.value || isInputElement.value ? fallbackFontSize : finalFontSize,
        fontWeight,
        fontStyle,
        textDecoration,
      };

      const paddingValue = paddingUniform ?? DEFAULT_SPACING.padding.top;
      const nextMargin = {
        top: marginTop ?? 0,
        right: marginRight ?? 0,
        bottom: marginBottom ?? 0,
        left: marginLeft ?? 0,
      };
      const currentMargin = currentProperties.margin;
      const didMarginChange =
        currentMargin?.top !== nextMargin.top ||
        currentMargin?.right !== nextMargin.right ||
        currentMargin?.bottom !== nextMargin.bottom ||
        currentMargin?.left !== nextMargin.left;

      return {
        properties: {
          ...currentProperties,
          ...fontProperties,
          textAlign:
            isFixedTextElement.value || isInputElement.value ? textAlign ?? 'left' : undefined,
          width: width !== undefined && width < 100 ? width : undefined,
          color,
          backgroundColor,
          padding: {
            top: paddingValue,
            right: paddingValue,
            bottom: paddingValue,
            left: paddingValue,
          },
          margin: nextMargin,
          autoHeaderSpacing: didMarginChange ? undefined : currentProperties.autoHeaderSpacing,
        },
      };
    },
  });

  function handleTextTypeChange(value: string | number | bigint | Record<string, unknown> | null) {
    if (value == null) return;
    const type = typeof value === 'string' ? value : String(value);
    if (!type) return;
    const size = TEXT_TYPE_SIZES[type as TextType];
    if (size !== undefined) {
      setValues({ textType: type as TextType, fontSize: size });
    }
  }

  const currentAlignIcon = computed(() => {
    const icons = {
      left: TextAlignLeftIcon,
      center: TextAlignCenterIcon,
      right: TextAlignRightIcon,
      justify: TextAlignJustifyIcon,
    };
    return icons[values?.textAlign ?? 'left'] ?? TextAlignLeftIcon;
  });

  const { toggleFontWeight, toggleFontStyle, toggleUnderline, toggleStrikethrough } =
    useTextFormatToggles({
      values: toRef(() => values),
      setValues,
    });

  const widthRangeEl = ref<HTMLInputElement | null>(null);
  const paddingRangeEl = ref<HTMLInputElement | null>(null);
  const widthTrackWidth = ref(192);
  const paddingTrackWidth = ref(192);

  onMounted(() => {
    if (widthRangeEl.value) widthTrackWidth.value = widthRangeEl.value.offsetWidth;
    if (paddingRangeEl.value) paddingTrackWidth.value = paddingRangeEl.value.offsetWidth;
  });

  function thumbCorrectedFill(value: number, min: number, max: number, trackWidth: number): string {
    const raw = (value - min) / (max - min);
    const corrected = ((raw * (trackWidth - 16) + 8) / trackWidth) * 100;
    return `${corrected}%`;
  }

  const widthProgress = computed(() =>
    thumbCorrectedFill(values?.width ?? 100, 10, 100, widthTrackWidth.value)
  );
  const paddingProgress = computed(() =>
    thumbCorrectedFill(values?.paddingUniform ?? 0, 0, 100, paddingTrackWidth.value)
  );

  const widthInputText = ref(String(values?.width ?? 100));
  const paddingInputText = ref(String(values?.paddingUniform ?? 0));

  watch(
    () => values?.width,
    (v) => {
      widthInputText.value = String(v ?? 100);
    }
  );
  watch(
    () => values?.paddingUniform,
    (v) => {
      paddingInputText.value = String(v ?? 0);
    }
  );

  function onWidthTextInput(e: Event): void {
    widthInputText.value = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 3);
  }

  function commitWidthValue(): void {
    const parsed = Number.parseInt(widthInputText.value, 10);
    if (Number.isNaN(parsed) || widthInputText.value === '') {
      setValues({ width: 100 });
      widthInputText.value = '100';
    } else {
      const clamped = Math.min(100, Math.max(10, parsed));
      setValues({ width: clamped });
      widthInputText.value = String(clamped);
    }
  }

  function onPaddingTextInput(e: Event): void {
    paddingInputText.value = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 3);
  }

  function commitPaddingValue(): void {
    const parsed = Number.parseInt(paddingInputText.value, 10);
    if (Number.isNaN(parsed) || paddingInputText.value === '') {
      setValues({ paddingUniform: 0 });
      paddingInputText.value = '0';
    } else {
      const clamped = Math.min(100, Math.max(0, parsed));
      setValues({ paddingUniform: clamped });
      paddingInputText.value = String(clamped);
    }
  }
</script>

<template>
  <div class="flex flex-col">
    <template v-if="isDesignSection">
      <!-- FixedText: Tipo de campo (first in Design) -->
      <div
        v-if="isFixedTextElement"
        class="border-b border-gray-02 py-4"
      >
        <FormField
          v-slot="{ componentField }"
          name="textType"
        >
          <FormItem class="flex flex-col gap-2">
            <FormLabel class="text-sm text-gray-11 font-normal">
              Tipo de campo
            </FormLabel>
            <Select
              :model-value="componentField.modelValue"
              @update:model-value="handleTextTypeChange"
            >
              <FormControl>
                <SelectTrigger class="h-11 w-full">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="simple">
                  Texto simples
                </SelectItem>
                <SelectItem value="h1">
                  Título 1
                </SelectItem>
                <SelectItem value="h2">
                  Título 2
                </SelectItem>
                <SelectItem value="h3">
                  Título 3
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <!-- Largura do elemento -->
      <div class="border-b border-gray-02 py-4 flex flex-col gap-4">
        <div class="flex items-start justify-between">
          <p class="text-sm text-gray-11 leading-5">
            Largura do<br>elemento
          </p>
          <div
            class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] w-[90px]"
          >
            <input
              type="text"
              inputmode="numeric"
              maxlength="3"
              min="10"
              max="100"
              placeholder="100"
              :value="widthInputText"
              class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
              @focus="($event.target as HTMLInputElement).select()"
              @input="onWidthTextInput"
              @blur="commitWidthValue"
              @keydown.enter="commitWidthValue"
            >
            <span class="text-gray-03 text-sm shrink-0">px</span>
          </div>
        </div>
        <input
          ref="widthRangeEl"
          type="range"
          min="10"
          max="100"
          :value="values?.width ?? 100"
          :style="{ '--val': widthProgress }"
          class="slider-track w-full"
          @input="
            (e) =>
              setValues({
                width: Math.min(100, Math.max(0, Number((e.target as HTMLInputElement).value))),
              })
          "
        >
      </div>

      <!-- Espaçamento -->
      <div class="border-b border-gray-02 py-4 flex flex-col gap-2">
        <p class="text-sm font-semibold text-gray-11">
          Espaçamento
        </p>

        <!-- Interno -->
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-11">
              Interno
            </p>
            <div
              class="bg-white-01 border border-gray-02 flex h-[42px] items-center justify-between overflow-hidden pl-4 pr-3 py-2.5 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] w-[90px]"
            >
              <input
                type="text"
                inputmode="numeric"
                maxlength="3"
                min="0"
                max="100"
                placeholder="0"
                :value="paddingInputText"
                class="border-0 outline-none p-0 w-full text-sm text-gray-07 bg-transparent"
                @focus="($event.target as HTMLInputElement).select()"
                @input="onPaddingTextInput"
                @blur="commitPaddingValue"
                @keydown.enter="commitPaddingValue"
              >
              <span class="text-gray-03 text-sm shrink-0">px</span>
            </div>
          </div>
          <input
            ref="paddingRangeEl"
            type="range"
            min="0"
            max="100"
            :value="values?.paddingUniform ?? 0"
            :style="{ '--val': paddingProgress }"
            class="slider-track w-full"
            @input="
              (e) =>
                setValues({
                  paddingUniform: Math.min(
                    100,
                    Math.max(0, Number((e.target as HTMLInputElement).value))
                  ),
                })
            "
          >
        </div>

        <!-- Externo -->
        <MarginControl
          :model-value="marginValues"
          :element-id="elementId"
          @update:model-value="onMarginUpdate"
        />
      </div>

      <!-- Input elements: Fonte Rótulo -->
      <div
        v-if="isInputElement"
        class="border-b border-gray-02 py-4 flex flex-col gap-2"
      >
        <p class="text-sm text-gray-11">
          Fonte Rótulo
        </p>

        <!-- B / I / U / S pill buttons -->
        <div class="bg-gray-02 flex gap-0.5 h-[42px] items-center p-[2px] rounded-[8px] w-full">
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.fontWeight === 'bold'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Negrito"
            @click="toggleFontWeight"
          >
            <BoldFontIcon class="pointer-events-none size-6" />
          </button>
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.fontStyle === 'italic'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Itálico"
            @click="toggleFontStyle"
          >
            <ItalicFontIcon class="pointer-events-none size-6" />
          </button>
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.textDecoration === 'underline'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Sublinhado"
            @click="toggleUnderline"
          >
            <UnderscoreFontIcon class="pointer-events-none size-6" />
          </button>
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.textDecoration === 'line-through'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Tachado"
            @click="toggleStrikethrough"
          >
            <StrikethroughFontIcon class="pointer-events-none size-6" />
          </button>
        </div>

        <!-- Font size + alignment row -->
        <div class="flex items-center gap-[10px]">
          <input
            type="number"
            min="8"
            max="72"
            placeholder="10"
            :value="values?.fontSize ?? 10"
            class="flex-1 h-[42px] bg-white-01 border border-gray-02 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] pl-3 pr-1 text-sm text-gray-07 outline-none min-w-0"
            @input="
              (e) =>
                setValues({
                  fontSize: Number((e.target as HTMLInputElement).value),
                })
            "
            @blur="
              (e) => {
                const v = Number((e.target as HTMLInputElement).value);
                if (v < 8) setValues({ fontSize: 8 });
                else if (v > 72) setValues({ fontSize: 72 });
              }
            "
          >

          <div class="w-[68px]">
            <FormField
              v-slot="{ componentField }"
              name="textAlign"
            >
              <Select v-bind="componentField">
                <FormControl>
                  <SelectTrigger class="h-11 w-full">
                    <IconImage
                      :src="currentAlignIcon"
                      alt="Alinhamento do texto"
                      class="size-5 text-gray-03"
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent class="min-w-0">
                  <SelectItem
                    value="left"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignLeftIcon"
                      alt="Esquerda"
                      class="size-5"
                    />
                  </SelectItem>
                  <SelectItem
                    value="center"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignCenterIcon"
                      alt="Centro"
                      class="size-5"
                    />
                  </SelectItem>
                  <SelectItem
                    value="right"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignRightIcon"
                      alt="Direita"
                      class="size-5"
                    />
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormField>
          </div>
        </div>

        <!-- Color control (A icon) -->
        <FormField
          v-slot="{ field, handleChange }"
          name="color"
        >
          <FormItem class="flex flex-col gap-2">
            <FormControl>
              <div class="flex items-center gap-2 h-[42px]">
                <div
                  class="relative flex items-center justify-center size-8 shrink-0 cursor-pointer"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="pointer-events-none shrink-0"
                  >
                    <path
                      d="M13.7498 12.5L10.4165 2.5H9.58317L6.24984 12.5M7.08317 10H12.9165"
                      stroke="var(--color-gray-07)"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1.6665 16.6667H18.3332"
                      :stroke="(field.value as string) || '#000000'"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
                >
                  <div
                    class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
                    :style="{ backgroundColor: (field.value as string) || '#ffffff' }"
                  >
                    <input
                      type="color"
                      :value="(field.value as string) || '#000000'"
                      class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      @input="(e) => handleChange((e.target as HTMLInputElement).value)"
                    >
                  </div>
                  <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
                  <input
                    :value="((field.value as string) || '').replace('#', '')"
                    type="text"
                    class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
                    placeholder="000000"
                    @input="(e) => handleChange('#' + (e.target as HTMLInputElement).value)"
                  >
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <!-- FixedText: Fonte section (redesigned) -->
      <div
        v-if="isFixedTextElement"
        class="border-b border-gray-02 py-4 flex flex-col gap-2"
      >
        <p class="text-sm text-gray-11">
          Fonte
        </p>

        <!-- B / I / U / S pill buttons -->
        <div class="bg-gray-02 flex gap-0.5 h-[42px] items-center p-[2px] rounded-[8px] w-full">
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.fontWeight === 'bold'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Negrito"
            @click="toggleFontWeight"
          >
            <BoldFontIcon class="pointer-events-none size-6" />
          </button>
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.fontStyle === 'italic'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Itálico"
            @click="toggleFontStyle"
          >
            <ItalicFontIcon class="pointer-events-none size-6" />
          </button>
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.textDecoration === 'underline'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Sublinhado"
            @click="toggleUnderline"
          >
            <UnderscoreFontIcon class="pointer-events-none size-6" />
          </button>
          <button
            type="button"
            :class="[
              'flex flex-1 h-full items-center justify-center p-1 rounded-md transition-all',
              values?.textDecoration === 'line-through'
                ? 'bg-white-01 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]'
                : '',
            ]"
            title="Tachado"
            @click="toggleStrikethrough"
          >
            <StrikethroughFontIcon class="pointer-events-none size-6" />
          </button>
        </div>

        <!-- Font size + alignment row -->
        <div class="flex items-center gap-[10px]">
          <input
            type="number"
            min="8"
            max="72"
            placeholder="10"
            :value="values?.fontSize ?? 10"
            class="flex-1 h-[42px] bg-white-01 border border-gray-02 rounded-lg shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] pl-3 pr-1 text-sm text-gray-07 outline-none min-w-0"
            @input="
              (e) =>
                setValues({
                  fontSize: Number((e.target as HTMLInputElement).value),
                })
            "
            @blur="
              (e) => {
                const v = Number((e.target as HTMLInputElement).value);
                if (v < 8) setValues({ fontSize: 8 });
                else if (v > 72) setValues({ fontSize: 72 });
              }
            "
          >

          <div class="w-[68px]">
            <FormField
              v-slot="{ componentField }"
              name="textAlign"
            >
              <Select v-bind="componentField">
                <FormControl>
                  <SelectTrigger class="h-11 w-full">
                    <IconImage
                      :src="currentAlignIcon"
                      alt="Alinhamento do texto"
                      class="size-5 text-gray-03"
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent class="min-w-0">
                  <SelectItem
                    value="left"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignLeftIcon"
                      alt="Esquerda"
                      class="size-5"
                    />
                  </SelectItem>
                  <SelectItem
                    value="center"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignCenterIcon"
                      alt="Centro"
                      class="size-5"
                    />
                  </SelectItem>
                  <SelectItem
                    value="right"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignRightIcon"
                      alt="Direita"
                      class="size-5"
                    />
                  </SelectItem>
                  <SelectItem
                    value="justify"
                    class="pr-2"
                  >
                    <IconImage
                      :src="TextAlignJustifyIcon"
                      alt="Justificado"
                      class="size-5 text-gray-03"
                    />
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormField>
          </div>
        </div>

        <!-- Color control (no section label for FixedText) -->
        <FormField
          v-slot="{ field, handleChange }"
          name="color"
        >
          <FormItem class="flex flex-col gap-2">
            <FormControl>
              <div class="flex items-center gap-2 h-[42px]">
                <div
                  class="relative flex items-center justify-center size-8 shrink-0 cursor-pointer"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="pointer-events-none shrink-0"
                  >
                    <path
                      d="M13.7498 12.5L10.4165 2.5H9.58317L6.24984 12.5M7.08317 10H12.9165"
                      stroke="var(--color-gray-07)"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1.6665 16.6667H18.3332"
                      :stroke="(field.value as string) || '#000000'"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
                >
                  <div
                    class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
                    :style="{ backgroundColor: (field.value as string) || '#ffffff' }"
                  >
                    <input
                      type="color"
                      :value="(field.value as string) || '#000000'"
                      class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      @input="(e) => handleChange((e.target as HTMLInputElement).value)"
                    >
                  </div>
                  <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
                  <input
                    :value="((field.value as string) || '').replace('#', '')"
                    type="text"
                    class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
                    placeholder="000000"
                    @input="(e) => handleChange('#' + (e.target as HTMLInputElement).value)"
                  >
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <!-- Non-FixedText: Cor do Rótulo -->
      <div
        v-if="!isFixedTextElement && !isInputElement"
        class="border-b border-gray-02 py-4 flex flex-col gap-[10px]"
      >
        <FormField
          v-slot="{ field, handleChange }"
          name="color"
        >
          <FormItem class="flex flex-col gap-2">
            <FormLabel class="text-sm text-gray-11 font-normal">
              Cor do Rótulo
            </FormLabel>
            <FormControl>
              <div class="flex items-center gap-2 h-[42px]">
                <div
                  class="relative flex items-center justify-center size-8 shrink-0 cursor-pointer"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="pointer-events-none shrink-0"
                  >
                    <path
                      d="M13.7498 12.5L10.4165 2.5H9.58317L6.24984 12.5M7.08317 10H12.9165"
                      stroke="var(--color-gray-07)"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1.6665 16.6667H18.3332"
                      :stroke="(field.value as string) || '#000000'"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div
                  class="flex-1 flex items-center gap-2 h-[42px] bg-white-01 border border-gray-02 rounded-lg pl-4 pr-3 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] overflow-hidden"
                >
                  <div
                    class="relative size-6 rounded border border-gray-02 shrink-0 cursor-pointer overflow-hidden"
                    :style="{ backgroundColor: (field.value as string) || '#ffffff' }"
                  >
                    <input
                      type="color"
                      :value="(field.value as string) || '#000000'"
                      class="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                      @input="(e) => handleChange((e.target as HTMLInputElement).value)"
                    >
                  </div>
                  <span class="text-gray-03 font-medium text-sm shrink-0">#</span>
                  <input
                    :value="((field.value as string) || '').replace('#', '')"
                    type="text"
                    class="flex-1 text-sm text-gray-07 bg-transparent outline-none border-none min-w-0"
                    placeholder="000000"
                    @input="(e) => handleChange('#' + (e.target as HTMLInputElement).value)"
                  >
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <!-- Non-FixedText: Font controls (non-input elements only) -->
      <div
        v-if="!isFixedTextElement && !isInputElement"
        class="border-b border-gray-02 py-4 flex flex-col gap-4"
      >
        <p class="text-sm text-gray-11">
          Fonte
        </p>
        <FormField
          v-slot="{ componentField }"
          name="headingLevel"
        >
          <FormItem>
            <Select v-bind="componentField">
              <FormControl>
                <SelectTrigger class="h-11 w-full">
                  <SelectValue placeholder="Selecione o tamanho" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="text5">
                  Texto Ultra Micro (8px)
                </SelectItem>
                <SelectItem value="text4">
                  Texto Micro (9px)
                </SelectItem>
                <SelectItem value="h3">
                  Texto Mínimo (10px)
                </SelectItem>
                <SelectItem value="h2">
                  Texto Compacto (11px)
                </SelectItem>
                <SelectItem value="h1">
                  Texto Pequeno (12px)
                </SelectItem>
                <SelectItem value="h6">
                  Texto Normal (14px)
                </SelectItem>
                <SelectItem value="h5">
                  Texto Médio (16px)
                </SelectItem>
                <SelectItem value="h4">
                  Texto Grande (18px)
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        </FormField>

        <div class="font-style-grid">
          <button
            type="button"
            :class="['font-style-btn', values?.fontWeight === 'bold' ? 'active' : '']"
            title="Negrito"
            @click="toggleFontWeight"
          >
            <span class="font-bold text-base leading-none">B</span>
          </button>
          <button
            type="button"
            :class="['font-style-btn', values?.fontStyle === 'italic' ? 'active' : '']"
            title="Itálico"
            @click="toggleFontStyle"
          >
            <span class="italic text-base leading-none font-serif">I</span>
          </button>
          <button
            type="button"
            :class="['font-style-btn', values?.textDecoration === 'underline' ? 'active' : '']"
            title="Sublinhado"
            @click="toggleUnderline"
          >
            <span class="text-base leading-none underline decoration-1">U</span>
          </button>
          <button
            type="button"
            :class="['font-style-btn', values?.textDecoration === 'line-through' ? 'active' : '']"
            title="Tachado"
            @click="toggleStrikethrough"
          >
            <span class="text-base leading-none line-through">S</span>
          </button>
        </div>
      </div>
    </template>

    <template v-if="isExtrasSection">
      <!-- Tooltip -->
      <div class="border-b border-gray-02 py-4">
        <FormField
          v-slot="{ componentField }"
          name="tooltip"
        >
          <FormItem class="flex flex-col gap-2">
            <FormLabel class="text-sm text-gray-11 font-normal">
              Tooltip
            </FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                placeholder="Digite aqui"
                class="h-11"
                :maxlength="FORM_FIELD_CHARACTER_LIMITS.TOOLTIP"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>

      <!-- Nome do campo no Log -->
      <div class="border-b border-gray-02 py-4">
        <FormField
          v-slot="{ componentField }"
          name="logFieldName"
        >
          <FormItem class="flex flex-col gap-2">
            <FormLabel class="text-sm text-gray-11 font-normal">
              Nome do campo no Log:
            </FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                placeholder="Digite aqui"
                class="h-11"
                :maxlength="FORM_FIELD_CHARACTER_LIMITS.LOG_FIELD_NAME"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
      </div>
    </template>
  </div>
</template>

<style scoped>
  .slider-track {
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    background: linear-gradient(
      to right,
      var(--theme-secondary) var(--val, 0%),
      var(--color-gray-02) var(--val, 0%)
    );
    border-radius: 2px;
    outline: none;
    cursor: pointer;
  }

  .slider-track::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
  }

  .slider-track::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 8px;
    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    border: none;
  }

  .font-style-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    padding: 8px;
    background-color: var(--color-gray-01);
    border-radius: 0.375rem;
  }

  .font-style-btn {
    width: 100%;
    aspect-ratio: 1;
    border: 1px solid var(--color-gray-02);
    background-color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 4px;
    color: var(--color-gray-07);
  }

  .font-style-btn:hover {
    border-color: var(--theme-secondary-ink);
    background-color: var(--color-white-02);
  }

  .font-style-btn.active {
    border-color: var(--theme-secondary-ink);
    border-width: 2px;
    background-color: rgba(var(--theme-secondary-rgb, 251, 113, 133), 0.1);
  }
</style>
